import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { ModalService } from '../../../core/services/modal/modal.service';
import { ViewportService } from '@shared/common/services/viewport/viewport.service';
import { IModalOptions } from '../../../core/interfaces/info-modal.interface';
import { Subscription } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-info-modal',
  templateUrl: './info-modal.component.html',
  styleUrls: ['./info-modal.component.scss'],
  standalone: false
})
export class InfoModalComponent implements OnInit, OnDestroy, AfterContentChecked {
  element: any;
  hidden: boolean;
  bottomPosition = 0;
  @Input() id: string;
  @Output() closeModal = new EventEmitter<void>();
  @Input() darkTheme: boolean;
  @Input() cssModifier: string;
  @Input() modalOptions: IModalOptions = {
    disableMaxHeightChanges: false,
    fullScreen: false,
    canGoFullScreenOnMobile: false,
    modalSize: null,
    modalContentAdditionalCssClass: null,
    disableMaxHeight: false
  };

  @ViewChild('modalContent') modalContent: ElementRef;
  @Output() swipeEvent: EventEmitter<any> = new EventEmitter<any>();

  private routerSubscription: Subscription;

  constructor(
    public modalService: ModalService,
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly viewportService: ViewportService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.element = el.nativeElement;
  }

  ngOnInit(): void {
    if (!this.id) {
      console.error('modal must have an id');
      return;
    }

    this.element.addEventListener('click', (event) => {
      event.stopPropagation();

      /**
       TODO this has been added to support being able to click a toast while a modal is open and not close the modal.
       This case should be supported in migration to SparkUI
       */
      const toastElement = this.document.querySelector('.toast');
      if (toastElement?.contains(event.target as Node)) {
        return;
      }

      if (event.target.className === 'lm-modal__container' && this.id != 'checkin-upload-process') {
        this.close();
      }
    });

    this.element.id = this.id;

    this.modalService.add(this.element);
    this.modalService.setRender(this.renderer);
    this.closeBtnDefaultValue(this.modalOptions.showCloseBtn);

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.close();
      }
    });
  }

  ngAfterContentChecked() {
    if (window.innerHeight <= 640 && this.modalOptions.canGoFullScreenOnMobile) {
      this.modalOptions.fullScreen = true;
    }
  }

  ngOnDestroy(): void {
    this.modalService.remove(this.id);

    try {
      this.renderer.removeChild(document.body, this.element);
    } catch (e) {
      // Ignore DOM errors
    }

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  get isMobile() {
    return this.viewportService.isMobileDevice();
  }

  getTestId(block: string) {
    if (!this.id) return '';

    return `qa-${this.id}-${block}`;
  }

  close(): void {
    this.modalService.close(this.id);
    if (this.closeModal) {
      this.closeModal.next();
    }
  }

  closeBtnDefaultValue(value) {
    value !== null && value !== undefined
      ? (this.modalOptions.showCloseBtn = value)
      : (this.modalOptions.showCloseBtn = true);
    this.cdr.detectChanges();
  }

  swipeUp(e) {
    if (this.isMobile && this.modalOptions.canGoFullScreenOnMobile) {
      if (e.distance > 500) {
        this.modalOptions.fullScreen = true;
        this.modalContent.nativeElement.classList.add('full-screen');
      }
    }
    this.swipeEvent.emit({
      event: e,
      modalContent: this.modalContent,
      isMobile: this.isMobile,
      modalOptions: this.modalOptions
    });
  }

  swipeDown(e) {
    if (e.isFinal && !this.modalOptions.fullScreen && this.isMobile && e.distance > 50) {
      this.close();
    }

    if (!this.modalOptions.fullScreen) {
      this.modalContent.nativeElement.classList.remove('slide-up-animation');
      this.bottomPosition = -e.distance;
    }

    this.swipeEvent.emit({
      event: e,
      modalContent: this.modalContent,
      isMobile: this.isMobile,
      modalOptions: this.modalOptions
    });
  }

  panEnd(e) {
    this.bottomPosition = 0;
    if (!this.modalOptions.fullScreen) {
      this.modalContent.nativeElement.style.height = 'auto';
      this.resetModalAnimations();
    }
  }

  resetModalAnimations() {
    this.modalContent.nativeElement.classList.add('slide-up-animation');
  }

  innerHeight() {
    return window.innerHeight - 120;
  }

  onModalContentScroll() {
    if (!this.modalOptions.fullScreen && this.isMobile && !this.modalOptions.disableMaxHeightChanges) {
      this.modalContent.nativeElement.style.transition = 'all 0.2s ease-in';
      this.modalContent.nativeElement.style.maxHeight = '90%';
    }
  }
}
